import { motion } from "framer-motion";

export default function EtPrivacy() {
  // First Created - 22 Nov 2025
  // Author - Atul Pandey
  // Purpose - Privacy Policy page for Eternity / Trinity SaaS platform

  return (
    <section
      id="privacy"
      className="min-h-screen bg-black text-gray-200 py-20 px-4 sm:px-6 lg:px-8 font-poppins"
    >
      <div className="max-w-4xl mx-auto">
        {/* Page Heading */}
        <motion.header
          className="mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs uppercase tracking-widest text-orange-400 mb-2">
            Legal
          </p>
          <h1 className="text-3xl md:text-4xl font-bold text-orange-500 mb-3">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-400">
            Effective date: {/* You can update this when you go live */}{" "}
            <span className="font-light text-gray-300">[To be set]</span>
          </p>
        </motion.header>

        <div className="bg-white/5 border border-white/10 rounded-2xl shadow-xl p-6 sm:p-8 space-y-8 text-sm md:text-base leading-relaxed">
          {/* 1. Introduction */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              1. Introduction
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              This Privacy Policy explains how{" "}
              <span className="font-semibold">
                Eternity Solutions Corp. (&quot;Eternity&quot;, &quot;we&quot;,
                &quot;our&quot;, &quot;us&quot;)
              </span>{" "}
              collects, uses, and protects your information when you use our
              websites, the Trinity platform, and related services (collectively,
              the &quot;Services&quot;). By accessing or using the Services, you
              agree to this Privacy Policy.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              2. Information We Collect
            </h2>

            <h3 className="font-semibold text-gray-200 mt-3 mb-1">
              2.1 Information you provide to us
            </h3>
            <p className="text-gray-300 font-extralight text-xs mb-2">
              We collect information that you choose to provide directly,
              including:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 font-extralight text-xs">
              <li>Account details such as name, email address, and password.</li>
              <li>
                Profile and organization details such as role, company name, and
                preferences.
              </li>
              <li>
                Content you create or capture in Trinity – including prompts,
                notes, reflections, goals, plans, and other information you input
                into the platform.
              </li>
              <li>
                Information you submit via contact forms or support channels.
              </li>
            </ul>

            <h3 className="font-semibold text-gray-200 mt-4 mb-1">
              2.2 Information collected automatically
            </h3>
            <p className="text-gray-300 font-extralight text-xs mb-2">
              When you use our Services, we automatically collect certain
              technical and usage information, such as:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 font-extralight text-xs">
              <li>Device and browser information.</li>
              <li>IP address, time zone, and language preferences.</li>
              <li>
                Usage information such as pages viewed, features used, and
                interaction patterns.
              </li>
              <li>Cookie identifiers and similar technologies.</li>
            </ul>

            <h3 className="font-semibold text-gray-200 mt-4 mb-1">
              2.3 AI interaction data
            </h3>
            <p className="text-gray-300 font-extralight text-xs">
              Trinity uses AI to assist with insights, summaries, and guidance.
              The text and data you provide for AI interactions are processed
              solely to deliver these Services and improve your experience. We do
              not allow third-party AI providers to use your data for training
              their general models.
            </p>

            <h3 className="font-semibold text-gray-200 mt-4 mb-1">
              2.4 Third-party integrations
            </h3>
            <p className="text-gray-300 font-extralight text-xs">
              If you connect third-party accounts (for example, calendar or
              identity providers), we receive only the information necessary to
              enable that integration and operate the Service.
            </p>
          </section>

          {/* 3. How We Use Information */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              3. How We Use Your Information
            </h2>
            <p className="text-gray-300 font-extralight text-xs mb-2">
              We use the information we collect for purposes such as:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 font-extralight text-xs">
              <li>Providing, maintaining, and improving the Trinity platform.</li>
              <li>Personalizing your experience and AI-generated guidance.</li>
              <li>Authenticating your identity and securing your account.</li>
              <li>Responding to your requests and providing customer support.</li>
              <li>
                Monitoring usage, troubleshooting issues, and protecting against
                fraud or abuse.
              </li>
              <li>
                Analyzing aggregated usage patterns to improve features and
                performance.
              </li>
              <li>
                Communicating with you about updates, releases, and important
                notices.
              </li>
            </ul>
          </section>

          {/* 4. Sharing */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              4. How We Share Information
            </h2>
            <p className="text-gray-300 font-extralight text-xs mb-2">
              We do <span className="font-semibold">not sell</span> your personal
              information. We may share information in the following ways:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 font-extralight text-xs">
              <li>
                With service providers who help us operate the Services (such as
                hosting, storage, analytics, communication, and AI processing
                providers).
              </li>
              <li>
                With your organization or team, where your use of Trinity is
                managed under an enterprise account.
              </li>
              <li>
                When required to comply with applicable laws, regulations, legal
                processes, or government requests.
              </li>
              <li>
                When we believe it is necessary to protect the rights, property,
                or safety of Eternity, our users, or others.
              </li>
              <li>
                In connection with a merger, acquisition, or other corporate
                transaction, subject to appropriate safeguards.
              </li>
            </ul>
          </section>

          {/* 5. Cookies */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              5. Cookies and Similar Technologies
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              We use cookies and similar technologies to keep you signed in,
              remember your preferences, and understand how the Services are used.
              You can control cookies through your browser settings, but some
              features of the Services may not function properly if you disable
              certain cookies.
            </p>
          </section>

          {/* 6. Security */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              6. Data Security
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              We use commercially reasonable measures to protect your information,
              including encryption in transit, encrypted storage where
              appropriate, and restricted, role-based access. However, no system
              can be guaranteed to be 100% secure, and you are responsible for
              keeping your account credentials confidential.
            </p>
          </section>

          {/* 7. Retention */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              7. Data Retention
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              We retain your information for as long as your account or your
              organization&apos;s subscription remains active, or as needed to
              provide the Services, comply with legal obligations, resolve
              disputes, and enforce agreements. We may anonymize or aggregate
              data so that it no longer identifies you.
            </p>
          </section>

          {/* 8. Rights */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              8. Your Rights and Choices
            </h2>
            <p className="text-gray-300 font-extralight text-xs mb-2">
              Depending on your region, you may have certain rights regarding your
              personal information, such as:
            </p>
            <ul className="list-disc list-inside space-y-1 text-gray-300 font-extralight text-xs mb-2">
              <li>Accessing the personal data we hold about you.</li>
              <li>Updating or correcting your information.</li>
              <li>Requesting deletion of your personal data.</li>
              <li>
                Requesting export of your data in a portable format, where
                applicable.
              </li>
              <li>Opting out of non-essential communications.</li>
            </ul>
            <p className="text-gray-300 font-extralight text-xs">
              You can exercise these rights by contacting us using the details
              below, subject to applicable laws.
            </p>
          </section>

          {/* 9. International Transfers */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              9. International Data Transfers
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              Our Services may be operated from the United States and other
              locations where we or our service providers maintain facilities. By
              using the Services, you acknowledge that your information may be
              transferred to, stored in, and processed in those locations.
            </p>
          </section>

          {/* 10. Children */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              10. Children&apos;s Privacy
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              The Services are not intended for use by children under the age of
              16, and we do not knowingly collect personal information from
              children under 16. If you believe a child has provided us with
              personal information, please contact us so we can take appropriate
              action.
            </p>
          </section>

          {/* 11. Changes */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              11. Changes to This Policy
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              We may update this Privacy Policy from time to time. When we do, we
              will revise the &quot;Effective date&quot; above and may provide
              additional notice as appropriate. Your continued use of the Services
              after any changes become effective constitutes your acceptance of
              the revised Policy.
            </p>
          </section>

          {/* 12. Contact */}
          <section>
            <h2 className="text-xl font-semibold text-orange-400 mb-2">
              12. Contact Us
            </h2>
            <p className="text-gray-300 font-extralight text-xs">
              If you have questions about this Privacy Policy or how we handle
              your data, you can contact us at:
            </p>
            <p className="mt-2 text-gray-200">
              <span className="block font-semibold">
                Eternity Solutions Corp.
              </span>
              <span className="block">Belmont, CA, USA</span>
              <a
                href="mailto:trinity@eternity-solutions.com"
                className="block text-orange-400 hover:text-orange-300 mt-1"
              >
                trinity@eternity-solutions.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </section>
  );
}